/** @jsx jsx */
import { useContext, useRef, useEffect, useState, useCallback } from "react"
import { useTransition, animated as a } from "react-spring"
import { Textarea, Button, Spinner, jsx } from "theme-ui"

import { lockScroll } from "../../util"

import { Context } from "../../context/MainContext"
import { ShopifyContext } from "../../context/ShopifyContext"

import useClickObserver from "../../hooks/useClickObserver"
import useEventListener from "../../hooks/useEventListener"

const styles = {
  container: {
    overflow: "hidden",
    fontSize: ["24px", "32px"],
    position: "fixed",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontFamily: "secondary",
    zIndex: 100,
    margin: "0px",
    padding: "0px",
    top: "0px",
    left: "0px",
    width: "100vw",
    height: "100vh",
    height: "-webkit-fill-available",
    backgroundColor: "rgba(255, 255, 255, 0.8)",
    color: "#333333",
  },
  inner: {
    width: "100%",
    position: "absolute",
    maxWidth: ["100%", "100%", "675px"],
    minHeight: ["100%", "100%", `480px`],
    fontSize: "16px",
    padding: ["10px", "10px", "25px"],
    backgroundColor: "#FFFFFF",
    border: "8px solid #333333",
    display: "flex",
    flexDirection: "column",
    flexGrow: 1,
  },
  form: {
    flexGrow: 1,
    display: "flex",
    flexDirection: "column",
    textarea: {
      flexGrow: 1,
      fontSize: "17px",
      width: "100%",
      borderRadius: "0px",
      margin: "0px",
      textAlign: "left",
      border: "4px solid #333333",
    },
    button: {
      fontSize: "24px",
      width: "100%",
      border: "none",
      backgroundColor: "#333333",
      color: "#FFFFFF",
      borderRadius: "0px",
    },
  },
  closeButton: {
    fontSize: "18px",
    padding: "3px",
    backgroundColor: "transparent",
    color: "#333333",
  },
}

function NoteModal() {
  const {
    store: { isNoteModalOpen },
    toggleNoteModal,
  } = useContext(Context)
  const ref = useRef(null)

  const {
    checkout: { note },
    addNoteToCheckout,
  } = useContext(ShopifyContext)

  const [message, setMessage] = useState(note)
  const [loading, setLoading] = useState(false)

  const handleClose = () => {
    toggleNoteModal()
  }

  const closeHandler = useCallback(
    e => {
      if (e.key === "Escape" && isNoteModalOpen) handleClose()
    },
    [isNoteModalOpen, handleClose]
  )

  const handleSubmit = async () => {
    if (message !== note) {
      setLoading(true)
      await addNoteToCheckout(message)
      setLoading(false)
    }
    const t = setTimeout(() => {
      handleClose()
      clearTimeout(t)
    }, 250)
  }

  useEventListener("keydown", closeHandler)
  useClickObserver(ref, isNoteModalOpen, () => handleClose())

  useEffect(() => {
    lockScroll(isNoteModalOpen)
  }, [isNoteModalOpen])

  useEffect(() => {
    setMessage(note)
  }, [note])

  const transitions = useTransition(isNoteModalOpen, null, {
    from: { opacity: 0 },
    enter: { opacity: 1 },
    leave: { opacity: 0 },
    duration: 1000,
  })

  return transitions.map(
    ({ item, key, props }) =>
      item && (
        <a.div key={key} style={props} tabIndex="0" sx={styles.container}>
          <div ref={ref} sx={styles.inner}>
            <form
              sx={styles.form}
              onSubmit={e => {
                e.preventDefault()
                handleSubmit()
              }}
            >
              <Textarea
                placeholder="Type your gift note here..."
                value={message}
                onChange={e => setMessage(e.target.value)}
                type="text"
              />
              <Button type="submit">
                {loading ? (
                  <Spinner
                    sx={{ color: "#FFFFFF" }}
                    size={32}
                    strokeWidth={3}
                  />
                ) : !!message ? (
                  "Finish"
                ) : (
                  "Close"
                )}
              </Button>
            </form>
          </div>
        </a.div>
      )
  )
}

export default NoteModal
